import { User } from 'src/app/interfaces/api/user';
import * as THREE from 'three';
import { NameMapping, PropertyMapping } from '../shared/mapping';
import { Project } from './project';

export const defaultModel: Model = {
  id: '',
  type: '3d',
  title: '',
  description: '',
  showDisconnectedUnits: true,
  urn: '',
  status: '',
  error_message: '',
  name: '',
  googleId: '',
  opacity: 1,
  user: '' as User | string,
  createdAt: Date.now(),
  availableFrom: Date.now(),
  availableTo: Date.now(),
  projects: [],
  isSelected: false,
  mapImage: '',
  minUnitScale: 1,
  metricZOffset: 0,
  concretingZOffset: 0,
  origin: {
    minUnitScale: 1,
    valueType: 'meters',
    position: {
      x: 0,
      y: 0,
      z: 0,
      mapLat: 0,
      mapLon: 0,
      lat: 0,
      lon: 0,
    },
    axis: 'Y',
    mapAngle: 0,
    gMapsAngle: 0,
    zoom: 18,
    zoomEnabled: true,
    panEnabled: true,
    latLonAngle: 0,
    angle: 0,
    xAngle: 0,
    yAngle: 0,
    zAngle: 0,
    scale: 1,
    up: 'Z',
    markers: {},
  },
  offset: {
    x: 0,
    y: 0,
    z: 0,
  },
  lastUpdate: Date.now(),
  thumbnail: '',
};

export interface ModelOrigin {
  minUnitScale: number;
  valueType: string;
  position: {
    x: number;
    y: number;
    z: number;
    mapLat: number;
    mapLon: number;
    lat: number;
    lon: number;
  };
  axis: string;
  mapAngle: number;
  gMapsAngle: number;
  zoom: number;
  zoomEnabled: boolean;
  panEnabled: boolean;
  latLonAngle: number;
  angle: number;
  xAngle: number;
  yAngle: number;
  zAngle: number;
  scale: number;
  up: string;
  markers: any;
  orientation?: THREE.Vector3;
  // Additional properties used in viewer
  projectLatLonXY?: [number, number];
  projectXY?: [number, number];
}

export interface PlanningOffset {
  concreting: number;
  wall: number;
  other: number;
}

export interface ImageMask {
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  mode: "delete" | "keep";
}

export interface MapCoord {
  north: number;
  east: number;
  south: number;
  west: number;
}

export interface Model {
  id: string;
  type?: 'map' | '2d' | '3d';
  title?: string;
  description?: string;
  showDisconnectedUnits: boolean;
  urn?: string;
  status?: string;
  error_message?: string;
  name?: string;
  googleId?: string;
  user?: User | string;
  createdAt?: string | number;
  availableFrom: string | number;
  availableTo: string | number;
  projects?: Project[] | string[];
  isSelected: boolean;
  resizedImage?: {
    angle?: number;
    src?: string;
  };
  opacity: number;
  mapImageAngle?: number;
  mapImage: string;
  mapImageSrc?: string; // New system
  mapCenter?: {
    lat: number;
    lon: number;
  };
  mapCoord?: MapCoord;
  imageTransform?: {
    angle?: number;
    opacity?: number;
    masks?: ImageMask[];
  };
  minUnitScale: number;
  metricZOffset: number;
  concretingZOffset: number;
  mapping?: {
    name?: string;
    volume?: string;
    length?: string;
    width?: string;
    height?: string;
    altitude?: string;
    type?: string;
    level?: string;
    zone?: string;
    planning?: {
      taskId?: string;
    }
  };
  origin: ModelOrigin;
  offset: {
    x: number;
    y: number;
    z: number;
  };
  planningOffset?: PlanningOffset;
  levels?: {
    build?: string;
    name?: string;
    z?: number;
    height?: number;
  }[];
  zones?: string[];
  lastUpdate: string | number;
  thumbnail: string;
  buildingTypes?: { [key: string]: string[] };
}

export interface ModelProperty {
  category: string | null;
  names: Array<string | NameMapping>;
}

export interface ExtendedModel extends Model {
  buildingTypes?: { [key: string]: string[] };
  mapping?: { [category: string]: PropertyMapping };
}

export interface PostModel {
  type?: 'map' | '2d' | '3d';
  title?: string;
  description?: string;
  showDisconnectedUnits: boolean;
  urn?: string;
  status?: string;
  error_message?: string;
  name: string;
  googleId?: string;
  user?: User | string;
  createdAt?: string | number;
  availableFrom: string | number;
  availableTo: string | number;
  projects?: Project[] | string[];
  isSelected: boolean;
  resizedImage?: {
    angle?: number;
    src?: string;
  };
  opacity: number;
  mapImageAngle?: number;
  mapImageSrc?: string; // New system
  mapImage?: string;
  mapCoord?: {
    north?: number;
    east?: number;
    south?: number;
    west?: number;
  };
  imageTransform?: {
    angle?: number;
    opacity?: number;
    masks?: ImageMask[];
  };
  minUnitScale: number;
  metricZOffset: number;
  concretingZOffset: number;
  mapping?: {
    name?: string;
    volume?: string;
    length?: string;
    width?: string;
    height?: string;
    altitude?: string;
    type?: string;
    level?: string;
    zone?: string;
  };
  origin: ModelOrigin;
  offset: {
    x: number;
    y: number;
    z: number;
  };
  planningOffset?: PlanningOffset;
  levels?: {
    build?: string;
    name?: string;
    z?: number;
  }[];
  zones?: string[];
  lastUpdate: string | number;
}

export interface PutModel {
  id: string;
  type?: 'map' | '2d' | '3d';
  title?: string;
  description?: string;
  showDisconnectedUnits?: boolean;
  urn?: string;
  status?: string;
  error_message?: string;
  name?: string;
  googleId?: string;
  user?: User | string;
  createdAt?: string | number;
  availableFrom?: string | number;
  availableTo?: string | number;
  projects?: Project[] | string[];
  isSelected?: boolean;
  resizedImage?: {
    angle?: number;
    src?: string;
  };
  opacity?: number;
  mapImageAngle?: number;
  mapImageSrc?: string;
  mapImage?: string;
  mapCoord?: {
    north?: number;
    east?: number;
    south?: number;
    west?: number;
  };
  imageTransform?: {
    angle?: number;
    opacity?: number;
    masks?: ImageMask[];
  };
  minUnitScale?: number;
  metricZOffset?: number;
  concretingZOffset?: number;
  mapping?: {
    name?: string;
    volume?: string;
    length?: string;
    width?: string;
    height?: string;
    altitude?: string;
    type?: string;
    level?: string;
    zone?: string;
  };
  origin?: {
    minUnitScale?: number;
    valueType?: string;
    position?: {
      x?: number;
      y?: number;
      z?: number;
      mapLat?: number;
      mapLon?: number;
      lat?: number;
      lon?: number;
    };
    axis?: string;
    mapAngle?: number;
    gMapsAngle?: number;
    zoom?: number;
    zoomEnabled?: boolean;
    panEnabled?: boolean;
    latLonAngle?: number;
    angle?: number;
    xAngle?: number;
    yAngle?: number;
    zAngle?: number;
    scale?: number;
    up?: string;
    markers?: any;
  };
  offset?: {
    x?: number;
    y?: number;
    z?: number;
  };
  planningOffset?: PlanningOffset;
  levels?: {
    build?: string;
    name?: string;
    z?: number;
  }[];
  zones?: string[];
  lastUpdate?: string | number;
}
